import { QueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

const NO_RETRY_STATUS = [400, 401, 403, 404];
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (failureCount > 3) return false;
        if (!isAxiosError(error)) return false;
        const statusCode = error.response?.status ?? 0;
        if (NO_RETRY_STATUS.includes(statusCode)) return false;
        // Do not retry if already retried by Axios
        // See ./src/api/client.ts
        const attempt = (error.config as any)?.attempt ?? 0;
        if (attempt > 0) return false;
        return true;
      },
      queryKeyHashFn: (queryKey) => {
        // Default tanstack query key hash function,
        // except that it ignores null values in the query key.
        // This is because backend treats null and undefined as the same,
        // and this implementation ensures key stability
        // when same query is called with null vs undefined values.
        return JSON.stringify(queryKey, (_, val) =>
          isPlainObject(val)
            ? Object.keys(val)
                .sort()
                .reduce((result, key) => {
                  result[key] = val[key];
                  return result;
                }, {} as any)
            : val === null
            ? undefined
            : val
        );
      }
    }
  }
});

// Copied from: https://github.com/jonschlinkert/is-plain-object
// eslint-disable-next-line @typescript-eslint/ban-types
export function isPlainObject(o: any): o is Object {
  if (!hasObjectPrototype(o)) {
    return false;
  }

  // If has modified constructor
  const ctor = o.constructor;
  if (typeof ctor === 'undefined') {
    return true;
  }

  // If has modified prototype
  const prot = ctor.prototype;
  if (!hasObjectPrototype(prot)) {
    return false;
  }

  // If constructor does not have an Object-specific method
  if (!Object.prototype.hasOwnProperty.call(prot, 'isPrototypeOf')) {
    return false;
  }

  // Most likely a plain Object
  return true;
}

function hasObjectPrototype(o: any): boolean {
  return Object.prototype.toString.call(o) === '[object Object]';
}
